import type { AnalyticsEvents } from '@sunflower-games/analytics-events';
import debounce from 'lodash/debounce';

import { analytics } from '../../services/analytics';

export const SEEN_TIME = 2000;
const DEBOUNCE_DELAY = 2000;

function getHeaderHeight() {
  const value = getComputedStyle(document.documentElement).getPropertyValue('--header-height').trim();
  return value.endsWith('px') ? parseFloat(value) : 0;
}

function getViewPort() {
  const headerHeight = getHeaderHeight();
  return {
    top: document.documentElement.scrollTop + headerHeight,
    bottom: document.documentElement.scrollTop + window.innerHeight,
    left: document.documentElement.scrollLeft,
    right: document.documentElement.scrollLeft + window.innerWidth,
  };
}

function sendGamesImpression(payload: AnalyticsEvents['games_impression']) {
  if (payload.slot_id.length > 0) {
    void analytics.sendEvent('games_impression', payload);
  }
}

function gridListener() {
  const viewPort = getViewPort();
  const row = document.querySelector<HTMLDivElement>('[data-games-row]');
  if (!row) {
    return;
  }
  const games = Array.from(row.querySelectorAll<HTMLDivElement>(`[data-game-id]`));
  if (!games.length) {
    return;
  }

  const payload: AnalyticsEvents['games_impression'] = {
    slot_id: [],
    category: [],
    category_position: [],
    vertical_location: [],
    horizontal_location: [],
  };

  const category = row.getAttribute('data-games-row')!;
  const categoryPosition = Number(row.getAttribute('data-category-position')) || 0;
  let currOffset = row.offsetTop;
  let vertPos = 1;
  let horPos = 1;
  for (let i = 0; i < games.length; i++) {
    const game = games[i];
    if (game.offsetTop > viewPort.bottom) {
      // If the game is below the viewport, we can stop the loop
      break;
    }
    if (currOffset < game.offsetTop) {
      vertPos += 1;
      horPos = 1;
      currOffset = game.offsetTop;
    }
    const top = game.getBoundingClientRect().top + window.scrollY;
    const height = game.getBoundingClientRect().height;
    const visibleHeight = Math.min(viewPort.bottom, top + height) - Math.max(viewPort.top, top);
    if ((visibleHeight / height) * 100 < 50) {
      continue;
    }
    const id = game.getAttribute('data-game-id');
    if (!id) {
      // If the game doesn't have an id, something is wrong, so we should stop the loop
      throw new Error('data-game-id is not set');
    }
    payload.slot_id.push(id);
    payload.category.push(category);
    payload.category_position.push(categoryPosition);
    payload.vertical_location.push(vertPos);
    payload.horizontal_location.push(horPos);
    horPos += 1;
  }

  sendGamesImpression(payload);
}

function lobbyListener() {
  const viewPort = getViewPort();

  const payload: AnalyticsEvents['games_impression'] = {
    slot_id: [],
    category: [],
    category_position: [],
    vertical_location: [],
    horizontal_location: [],
  };

  const allRows = Array.from(document.querySelectorAll<HTMLDivElement>('[data-games-row]'));

  allRows
    .filter((row) => {
      const top = row.getBoundingClientRect().top + window.scrollY;
      const bottom = row.getBoundingClientRect().bottom + window.scrollY;
      if (top > viewPort.top && bottom < viewPort.bottom) {
        return true;
      }
      const height = bottom - top;
      const visibleHeight = Math.min(viewPort.bottom, bottom) - Math.max(viewPort.top, top);
      return (visibleHeight / height) * 100 > 50;
    })
    .forEach((row) => {
      const rowLeft = row.getBoundingClientRect().left + window.scrollX;
      const rowRight = row.getBoundingClientRect().right + window.scrollX;
      const games = Array.from(row.querySelectorAll<HTMLDivElement>(`[data-game-id]`));
      if (!games.length) {
        return;
      }
      const category = row.getAttribute('data-games-row')!;
      const categoryPosition = allRows.indexOf(row) + 1;
      for (let i = 0; i < games.length; i++) {
        const game = games[i];
        const right = game.getBoundingClientRect().right + window.scrollX;
        // Skip games that are not visible
        if (right < rowLeft) {
          continue;
        }
        // Since games are sorted by horizontal position, we can break the loop here
        const left = game.getBoundingClientRect().left + window.scrollX;
        if (left > rowRight) {
          break;
        }
        const width = game.getBoundingClientRect().width;
        const visibleWidth = Math.min(rowRight, right) - Math.max(rowLeft, left);
        if ((visibleWidth / width) * 100 < 50) {
          continue;
        }
        const id = game.getAttribute('data-game-id');
        if (!id) {
          throw new Error('data-game-id is not set');
        }
        payload.slot_id.push(id);
        payload.category.push(category);
        payload.category_position.push(categoryPosition);
        payload.vertical_location.push(categoryPosition);
        payload.horizontal_location.push(i + 1);
      }
    });

  sendGamesImpression(payload);
}

// Only fire after the user has stopped scrolling for DEBOUNCE_DELAY.
export const seenGamesGridListener = debounce(gridListener, DEBOUNCE_DELAY);
export const seenGamesLobbyListener = debounce(lobbyListener, DEBOUNCE_DELAY);
